import { useTranslations } from 'next-intl';
import { memo } from 'react';

import { TpSlTriggerTimeIndicatorMapper } from '@flipster/features/src/tpsl-modal/constant';
import { useDeleteTpSlLegacy } from '@flipster/features/src/tpsl-modal/use-delete-tp-sl';
import { IconButton } from '@flipster/react/design-system/icon-button';
import { TextButton } from '@flipster/react/design-system/text-button';
import { css } from '@flipster/styled-system/css';
import { alert } from '@flipster/ui/Alert';
import { TableCell } from '@flipster/ui/Table';
import TrashIcon from '@flipster/ui/assets/svgs/filled/trash.svg';
import Num, { numberFormatter } from '@flipster/utils/num';
import { V2OrderSideType } from '@flipster/utils/types/base';

import {
  TimeTriggerTpslProps,
  useTimeTriggerTpslModal,
} from '@web/features/modals/TimeTriggerTpslModal/useTimeTriggerTpslModal';
import {
  pendingTimeTriggerTpslDeleteClickEvent,
  pendingTimeTriggerTpslDeleteConfirmClickEvent,
  pendingTimeTriggerTpslSettingClickEvent,
} from '@web/utils/trade-event';

const percentPrecision = 2;
const adjustTriggerTimeTpSlRatioForUserInput = (side?: V2OrderSideType, tpSlType?: 'tp' | 'sl', ratio?: string) => {
  if (!(side && tpSlType && ratio)) return '';
  return (
    TpSlTriggerTimeIndicatorMapper[side][tpSlType] === '+' ? Num(ratio).minus(1) : Num(ratio).multipliedBy(-1).plus(1)
  )
    .multipliedBy(100)
    .toString();
};

interface Props extends TimeTriggerTpslProps {
  side: V2OrderSideType;
  priceRatio: string | null;
  disabled?: boolean;
  initLeverage: number;
  triggerTimestamp: string;
  amount?: string;
  qty?: string;
}

/**
 * @deprecated Time-trigger TP/SL은 sunset된 기능이다. 신규 진입 경로는 없지만, 기존 유저가 잡아둔
 * time-trigger 주문이 남아 있을 수 있어 표시(UI)만 유지한다. 새 코드에서 사용하지 말 것.
 */
const TimeTriggerTpSlCol = memo(
  ({
    positionType,
    side,
    tpSlType,
    symbol,
    slot,
    priceRatio,
    disabled,
    initLeverage,
    triggerTimestamp,
    amount,
    qty,
  }: Props) => {
    const transPosition = useTranslations('Position.r250401');
    const transAssets = useTranslations('Assets.r250401');
    const transKyc = useTranslations('Kyc.r250311');
    const transCommon = useTranslations('Common.r250401');
    const { onOpenTimeTriggerTpslModal } = useTimeTriggerTpslModal();

    const [isLoading, deleteTpSl] = useDeleteTpSlLegacy();

    const hasPrice = !!priceRatio && priceRatio !== '0';
    const userInputPriceRatio = `${TpSlTriggerTimeIndicatorMapper[side][tpSlType]}${numberFormatter(
      adjustTriggerTimeTpSlRatioForUserInput(side, tpSlType, priceRatio ?? ''),
      {
        precision: percentPrecision,
        toFixed: true,
      },
    )}`;

    const onClickDeleteConfirmTpsl = () => {
      deleteTpSl(side, tpSlType, symbol, slot);
      pendingTimeTriggerTpslDeleteConfirmClickEvent({
        symbol,
        side,
        tpSlType,
        triggerTimestamp,
        triggerPriceRatio: userInputPriceRatio,
        amount,
        qty,
      });
    };

    return (
      <TableCell>
        <div>
          <TextButton
            as='button'
            size='small'
            color='neutral'
            disabled={disabled}
            label={hasPrice ? `${userInputPriceRatio}%` : transAssets('Set')}
            onClick={() => {
              onOpenTimeTriggerTpslModal({
                side,
                positionType,
                tpSlType,
                symbol,
                slot,
              });
              pendingTimeTriggerTpslSettingClickEvent({
                symbol,
                side,
                initLeverage,
                tpSlType,
                triggerTimestamp,
                triggerPriceRatio: userInputPriceRatio,
                amount,
                qty,
              });
            }}
          />
          {hasPrice && (
            <>
              <IconButton
                as='button'
                size='small'
                hierarchy='quaternary'
                icon={<TrashIcon width={20} height={20} />}
                disabled={disabled || isLoading}
                onClick={() => {
                  pendingTimeTriggerTpslDeleteClickEvent({
                    symbol,
                    side,
                    tpSlType,
                    triggerTimestamp,
                    triggerPriceRatio: userInputPriceRatio,
                    amount,
                    qty,
                  });
                  alert.show({
                    title: transPosition(tpSlType === 'tp' ? 'Remove_tp_title' : 'Remove_sl_title'),
                    message: transPosition(tpSlType === 'tp' ? 'Remove_tp_description' : 'Remove_sl_description'),
                    confirmText: transKyc('Proceed'),
                    onConfirm: onClickDeleteConfirmTpsl,
                    closeText: transCommon('Cancel'),
                    withClose: true,
                  });
                }}
              />
              <p className={css({ textStyle: 'Text/S/Regular' })}>{transAssets('From_entry_price')}</p>
            </>
          )}
        </div>
      </TableCell>
    );
  },
);

export default TimeTriggerTpSlCol;
